/**
 * SoilProgramReviewPage — the rebuilt programme review grid (U4).
 *
 * Pure presentation over ./soilReviewGrid: every grid decision (column
 * allocation, totals, duplicate detection, filtering, remaining issues) is a
 * data transform tested exhaustively in soilReviewGrid.test.ts. This page only
 * wires that module to pat-* primitives and the soilProgramService/
 * nutritionService network calls.
 *
 * Deliberate deviation from spec §4.6: Save and Activate live in a SaveBar
 * (decision 8, spec §7.1), not in the PageHeader — this is an editing surface
 * with a dirty state, and §7.1 requires primary form actions out of the
 * header. Only Delete (a destructive whole-record action) stays a header
 * action, behind a confirming Modal.
 *
 * The Activate label carries its own blocker count
 * (`t('...activateBlocked', { count })` -> "Activate · 3 to fix") instead of
 * being silently disabled for validation — the button is disabled ONLY while
 * saving or when the programme is already active.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '../shell/PageHeader';
import { Modal, Notice, FilterBar, SaveBar, StatusPill } from '../patterns';
import type { SaveBarStatus, FilterBarChip, StatusVariant } from '../patterns';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import {
  soilProgramService, SoilProgram, SoilProgramValidation,
} from '../../services/soilProgramService';
import { nutritionService } from '../../services/nutritionService';
import { naturalCompare } from '../../utils/naturalCompare';
import {
  GridState, buildState, orderedColumns, buildRows, columnTotals, grandTotal, setCell, deleteRow,
  deleteColumn, changeMonth, mapOrchard, mapProduct, toEntries, remainingIssues, duplicateMapping,
  filterRows, suggestionFor, columnHasError, displayProduct, columnsForDuplicateIssue,
  mappingsAfterRejection,
} from './soilReviewGrid';
import './nutrition.css';

const statusVariant = (status: SoilProgram['status']): StatusVariant =>
  (status === 'active' ? 'ok' : status === 'received' ? 'warn' : 'info');

const round2 = (n: number): number => Math.round(n * 100) / 100;

const emptyState: GridState = {
  orchards: [], columns: [], cells: {}, orchardMap: {}, productMap: {}, columnErrors: {},
};

const SoilProgramReviewPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [program, setProgram] = useState<SoilProgram | null>(null);
  const [state, setState] = useState<GridState>(emptyState);
  const [dbOrchards, setDbOrchards] = useState<string[]>([]);
  const [dbProducts, setDbProducts] = useState<string[]>([]);
  const [pendingOrchard, setPendingOrchard] = useState<Record<string, string>>({});
  const [pendingProduct, setPendingProduct] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');
  const [chip, setChip] = useState<'all' | 'errors' | 'empty'>('all');
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [dirty, setDirty] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [validation, setValidation] = useState<SoilProgramValidation | undefined>(undefined);
  const [loadError, setLoadError] = useState('');
  const [activateRejected, setActivateRejected] = useState(false);

  // Hooks must sit above every early return — a hook below a guard changes
  // hook order between renders and React throws.
  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('nutrition.title'), to: '/nutrition' },
    // Links back into the workspace must carry ?program=: the layout resolves
    // the board as "?program= if valid, else the tenant's ACTIVE programme"
    // (SoilWorkspaceLayout), so a bare path silently switches programmes (#429).
    // This page's route is soil-programs/:id/review, so :id IS the programme id.
    { label: t('nutrition.soil.workspace.title'), to: id ? `/nutrition/soil?program=${id}` : '/nutrition/soil' },
    { label: t('nutrition.soil.workspace.tab.setup'), to: id ? `/nutrition/soil/setup?program=${id}` : '/nutrition/soil/setup' },
    { label: t('nutrition.soil.crumb.review', { season: program?.season || '' }) },
  ]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    Promise.all([
      soilProgramService.get(id),
      soilProgramService.getOrchardNames().catch(() => []),
      nutritionService.listFertilizers().catch(() => []),
    ]).then(([p, dbo, ferts]) => {
      if (cancelled) return;
      setProgram(p);
      setValidation(p.validation);
      setState(buildState(p.entries));

      const sortedDb = dbo.slice().sort(naturalCompare);
      setDbOrchards(sortedDb);
      setDbProducts(ferts.map((f) => f.productName).slice().sort(naturalCompare));

      // Seed from the SERVER's suggestion only — never a locally computed one.
      const po: Record<string, string> = {};
      (p.validation?.unknownOrchards ?? []).forEach((o) => {
        const s = suggestionFor(p.validation, 'orchards', o);
        if (s) po[o] = s.suggestion;
      });
      const pp: Record<string, string> = {};
      (p.validation?.unknownProducts ?? []).forEach((pr) => {
        const s = suggestionFor(p.validation, 'products', pr);
        if (s) pp[pr] = s.suggestion;
      });
      setPendingOrchard(po);
      setPendingProduct(pp);
      setLoadError('');
    }).catch(() => {
      if (!cancelled) setLoadError(t('nutrition.soil.review.loadFailed'));
    });
    return () => { cancelled = true; };
    // `t` is deliberately absent: react-i18next's t is not referentially
    // stable and would re-fire this effect every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const applyOrchard = (orchardName: string) => {
    const target = pendingOrchard[orchardName];
    if (!target) return;
    setState((prev) => mapOrchard(prev, orchardName, target));
    setDirty(true);
    setActivateRejected(false);
  };

  const applyProduct = (productName: string) => {
    const target = pendingProduct[productName];
    if (!target) return;
    setState((prev) => mapProduct(prev, productName, target));
    setDirty(true);
    setActivateRejected(false);
  };

  const onDeleteRow = (orchardName: string) => {
    setState((prev) => deleteRow(prev, orchardName));
    setDirty(true);
    setActivateRejected(false);
  };

  const onDeleteColumn = (colId: string) => {
    setState((prev) => deleteColumn(prev, colId));
    setDirty(true);
    setActivateRejected(false);
  };

  const onChangeMonth = (colId: string, month: string) => {
    setState((prev) => changeMonth(prev, colId, month));
    setDirty(true);
    setActivateRejected(false);
  };

  // onSetCell is deliberately excluded: setCell only writes state.cells, which
  // remainingIssues never reads (it reads orchards/columns/orchardMap/
  // productMap) — editing a kg value cannot change remaining.total, so there
  // is nothing here for a stale activateRejected flag to disagree with.
  const onSetCell = (orchardName: string, colId: string, kg: number) => {
    setState((prev) => setCell(prev, orchardName, colId, kg));
    setDirty(true);
  };

  /**
   * Persist pending edits. Returns whether the stored programme now holds what
   * the user is looking at, so a caller can decide whether to continue.
   */
  const persist = async (): Promise<boolean> => {
    if (!id) return false;
    if (duplicateMapping(state)) {
      setSaveError(t('nutrition.soil.review.duplicateMapping'));
      return false;
    }
    setBusy(true); setSaveError('');
    try {
      const p = await soilProgramService.update(id, { entries: toEntries(state) });
      setProgram(p);
      setValidation(p.validation);
      setDirty(false);
      setActivateRejected(false);
      return true;
    } catch (_err) {
      setSaveError(t('nutrition.soil.review.saveFailed'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const onSave = async () => { await persist(); };

  const onActivate = async () => {
    if (!id) return;
    // Activate what the user is looking at. Without this the server validates
    // the stored rows, rejects them over mappings it was never sent, and the
    // catch below would discard exactly those mappings.
    //
    // `persisted` is tracked locally: persist() calls setDirty(false), which
    // does not update the `dirty` this invocation closed over.
    let persisted = !dirty;
    if (dirty) {
      persisted = await persist();
      // A failed save must not present as a failed activation. persist() has
      // already put its reason on screen.
      if (!persisted) return;
    }

    setBusy(true); setSaveError(''); setActivateRejected(false);
    try {
      await soilProgramService.activate(id);
      navigate('/nutrition');
    } catch (err) {
      const v = (err as { response?: { status?: number; data?: { validation?: SoilProgramValidation } } })
        ?.response?.data?.validation;
      if (v) {
        setValidation(v);
        setState((prev) => ({ ...prev, ...mappingsAfterRejection(prev, v, persisted) }));
        // Only a FLAG is set here. The message is assembled at render time from
        // remaining.total, because this branch also calls setState: a count
        // computed here would read the pre-clear state and disagree with the
        // number the issues panel and the Activate label then display.
        setActivateRejected(true);
      } else {
        setSaveError(t('nutrition.soil.review.activateFailed'));
      }
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!id) return;
    try {
      await soilProgramService.remove(id);
      setDeleteOpen(false);
      navigate('/nutrition/soil/setup');
    } catch (err) {
      setDeleteOpen(false);
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
      setSaveError(code === 'PROGRAM_ACTIVE'
        ? t('nutrition.soil.setup.programs.deleteBlockedActive')
        : t('nutrition.soil.review.saveFailed'));
    }
  };

  // Error branch returns BEFORE the loading guard, so a failed load renders a
  // notice rather than sitting on "loading" forever.
  if (loadError) {
    return <div className="pat-page"><Notice tone="danger" testId="soil-review-error">{loadError}</Notice></div>;
  }
  if (!program) return <div className="pat-page">{t('common.loading')}</div>;

  const remaining = remainingIssues(state, validation);
  const activateLabel = remaining.total === 0
    ? t('nutrition.soil.review.activate')
    : t('nutrition.soil.review.activateBlocked', { count: remaining.total });

  // Built here, not in the catch — see onActivate. This shares ONE expression
  // with activateLabel and the issues panel, so the three counts cannot drift.
  // A 0 count means the server rejected for something remainingIssues does not
  // model; the generic message is honest, "0 issues still need fixing" is not.
  const activateMessage = activateRejected
    ? (remaining.total > 0
      ? t('nutrition.soil.review.activateRejected', { count: remaining.total })
      : t('nutrition.soil.review.activateFailed'))
    : '';

  const orchardCount = state.orchards.length;
  const columns = orderedColumns(state);
  const productCount = Array.from(new Set(columns.map((c) => displayProduct(state, c)))).length;
  const kgTotal = round2(grandTotal(state));

  const rows = buildRows(state, validation);
  const filteredRows = filterRows(rows, search, chip);
  const colTotals = columnTotals(state);

  // FilterBarChip carries its own `count` field (rendered as " · {count}" by
  // FilterBar itself) — pass the count there, not baked into the label via
  // t() interpolation. `filter.all`/`errors`/`empty` are plain, uninterpolated
  // strings; a {{count}} placeholder in them would never be filled in.
  const chips: FilterBarChip[] = [
    { id: 'all', label: t('nutrition.soil.review.filter.all'), count: rows.length, active: chip === 'all' },
    { id: 'errors', label: t('nutrition.soil.review.filter.errors'), count: rows.filter((r) => r.hasError).length, active: chip === 'errors' },
    { id: 'empty', label: t('nutrition.soil.review.filter.empty'), count: rows.filter((r) => r.isEmpty).length, active: chip === 'empty' },
  ];

  const saveBarStatus: SaveBarStatus = saveError
    ? { kind: 'error', message: saveError }
    : activateMessage ? { kind: 'error', message: activateMessage }
      : busy ? { kind: 'saving' } : dirty ? { kind: 'dirty' } : null;

  // Build from an array of present segments and join with ' · ' — the vendor
  // and filename are both optional, so string concatenation with fixed
  // separators either leaves a dangling '·' when one is absent, or (the bug
  // found in the browser) drops the separator entirely between the kg total
  // and the filename. Joining only present segments avoids both failure modes.
  const metaSegments = [
    program.source?.vendor,
    t('nutrition.soil.review.meta', { orchards: orchardCount, products: productCount, kg: kgTotal }),
    program.source?.filename,
  ].filter(Boolean);

  const headerMeta = (
    <>
      <span className="ntr-rev-fix" style={{ margin: 0, display: 'inline-flex' }} data-testid="rev-status">
        <StatusPill variant={statusVariant(program.status)}>
          {t(`nutrition.soil.programs.statuses.${program.status}`, program.status)}
        </StatusPill>
      </span>
      <span> {metaSegments.join(' · ')}</span>
    </>
  );

  return (
    <div className="pat-page">
      <PageHeader
        title={t('nutrition.soil.review.title')}
        meta={headerMeta}
        actions={program.status !== 'active' ? (
          <button type="button" className="pat-btn pat-btn--danger" data-testid="rev-delete" onClick={() => setDeleteOpen(true)}>
            {t('nutrition.soil.review.delete')}
          </button>
        ) : undefined}
      />

      {remaining.total > 0 && (
        <Notice tone="danger" testId="soil-review-issues">
          <strong>{t('nutrition.soil.review.issuesTitle')}</strong>

          {remaining.unknownProducts.map((productName) => {
            const carryingColumns = state.columns.filter((c) => c.productName === productName);
            const suggestion = suggestionFor(validation, 'products', productName);
            return (
              <div key={`prod-${productName}`} className="ntr-rev-fix">
                <span className="ntr-rev-fix__name">{productName}</span>
                <select
                  data-testid={`fix-product-${productName}`}
                  value={pendingProduct[productName] ?? ''}
                  onChange={(e) => setPendingProduct((prev) => ({ ...prev, [productName]: e.target.value }))}
                >
                  <option value="">{t('nutrition.soil.review.chooseProduct')}</option>
                  {dbProducts.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
                {pendingProduct[productName] && suggestion && pendingProduct[productName] === suggestion.suggestion && (
                  <span>{t('nutrition.soil.review.suggested')}</span>
                )}
                <button
                  type="button" className="pat-btn pat-btn--subtle"
                  data-testid={`apply-product-${productName}`}
                  onClick={() => applyProduct(productName)}
                >
                  {t('nutrition.soil.review.fix.applyProduct')}
                </button>
                {carryingColumns.map((c) => (
                  <button
                    key={c.id} type="button" className="pat-btn pat-btn--danger"
                    data-testid={`issue-delete-col-product-${c.id}`}
                    onClick={() => onDeleteColumn(c.id)}
                  >
                    {t('nutrition.soil.review.deleteColumn')}
                  </button>
                ))}
              </div>
            );
          })}

          {remaining.unknownOrchards.map((orchardName) => {
            const suggestion = suggestionFor(validation, 'orchards', orchardName);
            return (
              <div key={`orch-${orchardName}`} className="ntr-rev-fix">
                <span className="ntr-rev-fix__name">{orchardName}</span>
                <select
                  data-testid={`fix-orchard-${orchardName}`}
                  value={pendingOrchard[orchardName] ?? ''}
                  onChange={(e) => setPendingOrchard((prev) => ({ ...prev, [orchardName]: e.target.value }))}
                >
                  <option value="">{t('nutrition.soil.review.chooseOrchard')}</option>
                  {dbOrchards.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
                {pendingOrchard[orchardName] && suggestion && pendingOrchard[orchardName] === suggestion.suggestion && (
                  <span>{t('nutrition.soil.review.suggested')}</span>
                )}
                <button
                  type="button" className="pat-btn pat-btn--subtle"
                  data-testid={`apply-orchard-${orchardName}`}
                  onClick={() => applyOrchard(orchardName)}
                >
                  {t('nutrition.soil.review.fix.applyOrchard')}
                </button>
                <button
                  type="button" className="pat-btn pat-btn--danger"
                  data-testid={`issue-delete-row-unknown-${orchardName}`}
                  onClick={() => onDeleteRow(orchardName)}
                >
                  {t('nutrition.soil.review.deleteRow')}
                </button>
              </div>
            );
          })}

          {remaining.duplicateColumns.flatMap(({ month, productName }) => (
            // Shared with remainingIssues (soilReviewGrid.ts) so the panel renders a
            // fix affordance for exactly the columns it just counted: raw identity
            // for a server-reported duplicate (mapping a product is NOT a resolution
            // path, so the columns must not vanish the moment the shared product
            // gets mapped via the OTHER fix), mapped identity for a collision that
            // only exists once the mapping is applied.
            columnsForDuplicateIssue(state, month, productName).map((c, i) => (
              <div key={`dupcol-${c.id}`} className="ntr-rev-fix">
                <span className="ntr-rev-fix__name">{productName} · {month}</span>
                <label>
                  {t('nutrition.soil.review.fix.changeMonthN', { n: i + 1 })}
                  <input
                    type="month"
                    data-testid={`issue-month-${c.id}`}
                    value={c.month}
                    onChange={(e) => onChangeMonth(c.id, e.target.value)}
                  />
                </label>
                <button
                  type="button" className="pat-btn pat-btn--danger"
                  data-testid={`issue-delete-col-dup-${c.id}`}
                  onClick={() => onDeleteColumn(c.id)}
                >
                  {t('nutrition.soil.review.fix.deleteColumnN', { n: i + 1 })}
                </button>
              </div>
            ))
          ))}

          {remaining.duplicateOrchardRows.map((orchardName) => (
            <div key={`duprow-${orchardName}`} className="ntr-rev-fix">
              <span className="ntr-rev-fix__name">{orchardName}</span>
              <button
                type="button" className="pat-btn pat-btn--danger"
                data-testid={`issue-delete-row-dup-${orchardName}`}
                onClick={() => onDeleteRow(orchardName)}
              >
                {t('nutrition.soil.review.deleteRow')}
              </button>
            </div>
          ))}
        </Notice>
      )}

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        chips={chips}
        onChipClick={(chipId) => setChip(chipId as 'all' | 'errors' | 'empty')}
      />

      <div className="pat-card ntr-rev-card">
        <div className="ntr-rev-scroll">
          <table className="ntr-rev">
            <thead>
              <tr>
                <th className="ntr-rev__corner">{t('nutrition.soil.review.orchard')}</th>
                {columns.map((c) => {
                  const hasError = columnHasError(state, c, validation);
                  return (
                    <th key={c.id} className={hasError ? 'ntr-rev__col--error' : ''}>
                      <div className="ntr-rev__colhead">
                        <input
                          type="month"
                          data-testid={`rev-month-${c.id}`}
                          value={c.month}
                          onChange={(e) => onChangeMonth(c.id, e.target.value)}
                        />
                        <span>{displayProduct(state, c)}</span>
                        <button
                          type="button" className="pat-btn pat-btn--danger ntr-rev__del-btn"
                          data-testid={`delete-col-${c.id}`}
                          onClick={() => onDeleteColumn(c.id)}
                        >
                          {t('nutrition.soil.review.deleteColumn')}
                        </button>
                        {state.columnErrors[c.id] && (
                          <div className="ntr-rev__colerror" role="alert">
                            {t('nutrition.soil.review.duplicateColumnEdit')}
                          </div>
                        )}
                      </div>
                    </th>
                  );
                })}
                <th>{t('nutrition.soil.review.rowTotal')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => (
                <tr key={row.orchardName} className={row.hasError ? 'ntr-rev__row--error' : ''}>
                  <td className="ntr-rev__rowhead" data-testid={`rev-rowhead-${row.orchardName}`}>
                    <div>
                      <span>{row.displayName}</span>
                      <button
                        type="button" className="pat-btn pat-btn--danger ntr-rev__del-btn"
                        data-testid={`delete-row-${row.orchardName}`}
                        onClick={() => onDeleteRow(row.orchardName)}
                      >
                        {t('nutrition.soil.review.deleteRow')}
                      </button>
                    </div>
                  </td>
                  {columns.map((c) => {
                    const k = `${row.orchardName}|${c.id}`;
                    return (
                      <td key={c.id}>
                        <input
                          type="number"
                          className="ntr-rev-cell"
                          data-testid={`rev-cell-${row.orchardName}-${c.id}`}
                          value={state.cells[k] ?? ''}
                          onChange={(e) => onSetCell(row.orchardName, c.id, Number(e.target.value))}
                        />
                      </td>
                    );
                  })}
                  <td className="ntr-rev__rowtotal" data-testid={`rev-rowtotal-${row.orchardName}`}>{round2(row.total)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="ntr-rev__totals">
                <td className="ntr-rev__totalhead">{t('nutrition.soil.review.columnTotal')}</td>
                {columns.map((c) => (
                  <td key={c.id} data-testid={`rev-coltotal-${c.id}`}>{round2(colTotals[c.id] || 0)}</td>
                ))}
                <td data-testid="rev-grandtotal">{kgTotal}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <SaveBar
        status={saveBarStatus}
        secondaryActions={[{
          label: t('nutrition.soil.review.save'),
          tone: 'subtle',
          onClick: onSave,
          disabled: busy || program.status === 'active',
        }]}
        primaryAction={{
          label: activateLabel,
          onClick: onActivate,
          disabled: busy || program.status === 'active',
        }}
      />

      <Modal
        show={deleteOpen}
        onHide={() => setDeleteOpen(false)}
        title={t('nutrition.soil.review.deleteConfirmTitle')}
      >
        <Modal.Body>{t('nutrition.soil.review.deleteConfirmBody')}</Modal.Body>
        <Modal.Footer>
          <button type="button" className="pat-btn pat-btn--subtle" onClick={() => setDeleteOpen(false)}>
            {t('common.cancel')}
          </button>
          <button type="button" className="pat-btn pat-btn--danger" data-testid="rev-delete-confirm" onClick={onDelete}>
            {t('nutrition.soil.review.delete')}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SoilProgramReviewPage;
